import type { RouteLocationNormalized } from 'vue-router';
import { RouteRecordRaw } from 'vue-router';
import BaseViewport from '@/common/components/templates/baseViewport/BaseViewport.vue';
import { VIEWPORT_TYPE } from '@/common/components/templates/baseViewport/baseViewport.define';
import { PATH, requireAuth, setDayjsTimezone } from '@/common/router/authGuard';
import { EvMessage } from 'evui';
import { i18n } from '@/common/locale';
import { getV8Dashboards } from '@/api-client/metav8Over/queries/integrated-dashboard-v8-controller/integrated-dashboard-v8-controller';
import { checkRole } from '@/common/auth/auth.utils';
import { useMyInfoStore } from '@/config/stores/my-info';
import {
  deniedDashboard,
  deniedToRouteToEditDashboard,
} from '@/dashboard/features/permission/composables/dashboard.utils';

export const checkDashboardExistById = async (
  to: RouteLocationNormalized,
): Promise<string | undefined> => {
  const { t } = i18n.global;
  const myRole = useMyInfoStore().myInfo.role;
  const isMaster = checkRole.isMaster(myRole);

  const dashboardId = to.params.id || to.query.id;
  if (dashboardId) {
    const { data } = await getV8Dashboards({
      dashboardId: Number(dashboardId),
      includeImage: false,
    });
    if (data.data?.length) {
      if (isMaster) {
        return undefined;
      }

      const { preset, featuredReport, shared, createUser } = data.data[0];
      if (
        deniedDashboard({
          isMyDashboard: !!createUser,
          isFeatured: preset,
          isFeatureReport: featuredReport,
        })
      ) {
        EvMessage({
          type: 'error',
          message: t('NOTI.API.NOT_PERMISSION'),
        });
        return PATH.DASHBOARD_LIST;
      }

      if (to.meta.mode === 'edit') {
        const deniedToAccessEdit = deniedToRouteToEditDashboard({
          isMyDashboard: !!createUser,
          isFeatured: preset,
          isFeatureReport: featuredReport,
        });
        const isEnableEdit = !deniedToAccessEdit && (createUser || (shared && !createUser));
        if (!isEnableEdit) {
          EvMessage({
            type: 'error',
            message: deniedToAccessEdit
              ? t('NOTI.API.NOT_PERMISSION')
              : t('MESSAGE.DASHBOARD_LIST.NO_PERMISSION_DASHBOARD'),
          });
          return PATH.DASHBOARD_LIST;
        }
        return undefined;
      }
      return undefined;
    }
  }
  EvMessage({
    type: 'error',
    message: t('MESSAGE.DASHBOARD_LIST.NO_PERMISSION_DASHBOARD'),
  });
  return PATH.DASHBOARD_LIST;
};

const PREFIX_DASHBOARD = 'dashboard_';

export const ROUTE_NAME_DASHBOARD = {
  VIEW: `${PREFIX_DASHBOARD}Dashboard View`,
  EDITOR: `${PREFIX_DASHBOARD}Dashboard Editor View`,
  LIST: `${PREFIX_DASHBOARD}Dashboard List`,
};

const dashboardMenu: RouteRecordRaw[] = [
  {
    path: '/dashboard/',
    name: `${PREFIX_DASHBOARD}Dashboard`,
    component: BaseViewport,
    meta: {
      viewport: VIEWPORT_TYPE.REALTIME_DASHBOARD,
    },
    children: [
      // 기존 대시보드 목록
      // {
      //   path: 'list',
      //   name: `${PREFIX_DASHBOARD}Dashboard List`,
      //   component: () =>
      //     import('@/dashboard/views/dashboardListView_deprecated/DashboardListView.vue'),
      //   beforeEnter: [requireAuth, setDayjsTimezone],
      // },

      // 신규개발 대시보드 목록
      {
        path: 'list',
        name: ROUTE_NAME_DASHBOARD.LIST,
        component: () => import('@/dashboardList/views/DashboardMainView.vue'),
        beforeEnter: [requireAuth, setDayjsTimezone],
      },
      {
        path: '',
        name: `${PREFIX_DASHBOARD}DashboardItem`,
        children: [
          {
            path: 'view/:id',
            name: ROUTE_NAME_DASHBOARD.VIEW,
            component: () => import('../views/dashboardView/DashboardView.vue'),
            beforeEnter: [requireAuth, setDayjsTimezone, checkDashboardExistById],
            meta: { mode: 'view', needCustomEnvPreFetch: true, needGlobalEnvPreFetch: true },
          },
          {
            path: 'editor/:id*',
            name: ROUTE_NAME_DASHBOARD.EDITOR,
            component: () => import('../views/dashboardEditorView/DashboardEditorView.vue'),
            beforeEnter: [requireAuth, setDayjsTimezone, checkDashboardExistById],
            meta: { mode: 'edit', needGlobalEnvPreFetch: true },
          },
        ],
      },
    ],
  },
];

const getDashboardNaviMenu = () => {
  const menus = [...dashboardMenu];
  if (menus?.[0].children?.length) {
    menus[0].children = menus[0].children.filter(({ path }) => path !== '');
  }

  return menus;
};

export { dashboardMenu, getDashboardNaviMenu };
